import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X, Share } from 'lucide-react';
import { useInstallPrompt } from '../lib/useInstallPrompt';

const DISMISS_KEY = 'myspan_install_dismissed_v1';

// NOTE: This localStorage key stores ONLY a numeric dismissal timestamp
// (Date.now()) used to suppress the PWA install banner for 14 days after the
// user closes it. It is NOT an auth token, session ID, or PII. Reset with
// `localStorage.removeItem('myspan_install_dismissed_v1')`.

/**
 * Floating install-prompt banner for the PWA.
 * - Android/desktop Chrome: uses the captured `beforeinstallprompt` event
 *   (see /lib/useInstallPrompt for the module-scope listener).
 * - iOS Safari: shows a hint ("tap Share → Add to Home Screen") since iOS
 *   does not fire beforeinstallprompt.
 * - Auto-hides when running standalone or when the user dismisses it (14 days).
 * - Users can re-open the install flow anytime from the sidebar "Install app"
 *   button which shares the same hook.
 */
export const InstallPrompt = () => {
  const { canPromptNatively, isIOS, isStandalone, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(true); // start hidden until we check localStorage

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const withinCooldown = dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000;
    setDismissed(!!withinCooldown);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const install = async () => {
    const outcome = await promptInstall();
    if (outcome === 'dismissed') dismiss();
    else setDismissed(true);
  };

  if (isStandalone || dismissed) return null;
  // Only surface the banner when we have something actionable:
  // - Android/desktop: a captured native prompt
  // - iOS: an instructional hint
  if (!canPromptNatively && !isIOS) return null;

  return (
    <div
      className="fixed inset-x-3 z-50 bottom-20 md:bottom-4 md:left-auto md:right-4 md:w-96 bg-white border border-[#e2e8f0] rounded-xl shadow-lg p-4 flex items-start gap-3"
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install My SPAN"
    >
      <div className="h-10 w-10 rounded-lg bg-[#2563eb] text-white flex items-center justify-center flex-shrink-0">
        <Download className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit' }}>
          Install My SPAN
        </p>
        {isIOS ? (
          <p className="text-xs text-[#64748b] mt-1 leading-relaxed">
            Tap <Share className="inline h-3 w-3 mx-0.5" /> Share, then <b>Add to Home Screen</b> to
            install this app on your iPhone.
          </p>
        ) : (
          <p className="text-xs text-[#64748b] mt-1">
            Add My SPAN to your home screen for a faster, app-like experience.
          </p>
        )}
        {!isIOS && (
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={install}
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white h-8"
              data-testid="install-app-button"
            >
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="h-8 text-[#64748b]"
              data-testid="dismiss-install-button"
            >
              Not now
            </Button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="text-[#94a3b8] hover:text-[#0f172a] p-1 -mt-1 -mr-1"
        aria-label="Close install prompt"
        data-testid="close-install-prompt"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default InstallPrompt;
